class SearchSpringBeaconClient {
  static config = {
    beaconBase: 'https://analytics.searchspring.net/beacon/v2',
    initiator: 'montirex/custom/1.0',
    siteId: window.themeVariables?.settings?.searchSpringIndexes?.DEFAULT || '',
    impressionThreshold: 0.7,
    impressionDelayMs: 1000,
    batchFlushDelayMs: 50
  };
  
  static isVisible(el) {
    const { display, visibility, opacity } = getComputedStyle(el);
    return display !== 'none' && visibility !== 'hidden' && opacity !== '0';
  }

  static productFromEl(el) {
    return {
      type: 'product',
      parentId: el.dataset.parentId,
      uid: el.dataset.uid,
      sku: el.dataset.sku
    };
  }

  buildContext() {
    const context = {
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      pageLoadId: window.pageLoadId || '',
      userId: SearchSpringCommon.getCookie('ssUserId') || '',
      sessionId: SearchSpringCommon.getCookie('ssSessionIdNamespace') || '',
      initiator: SearchSpringBeaconClient.config.initiator
    };

    const shopperId = SearchSpringCommon.getCookie('ssShopperId');
    if (shopperId) context.shopperId = shopperId;

    const currencyCode = window.themeVariables?.settings?.cartCurrency;
    if (currencyCode) context.currency = { code: currencyCode };

    if (SearchSpringCommon.isDevMode()) context.dev = true;

    return context;
  }

  url(contextType, eventType) {
    return `${SearchSpringBeaconClient.config.beaconBase}/${SearchSpringBeaconClient.config.siteId}/${contextType || 'search'}/${eventType}`;
  }

  async send(url, data) {
    const payload = { context: this.buildContext(), data };

    if (SearchSpringCommon.isDevMode()) {
      console.log('[SS Beacon dev]', url, JSON.parse(JSON.stringify(payload)));
    }

    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      });
    } catch (error) {
      console.error(error);
    }
  }
}

class ImpressionTracker {
  constructor(container, contextType, responseId, client, tag) {
    this.container = container;
    this.contextType = contextType;
    this.responseId = responseId;
    this.client = client;
    this.tag = tag;

    this.seenIds = new Set();
    this.viewTimers = new Map();
    this.impressionQueue = [];
    this.batchTimer = null;

    this.observer = new IntersectionObserver(
      entries => entries.forEach(e => this.onEntry(e)),
      { threshold: SearchSpringBeaconClient.config.impressionThreshold }
    );

    container.querySelectorAll('[data-product-id]').forEach(el => this.observer.observe(el));
  }

  flushQueue() {
    if (this.batchTimer) return;

    this.batchTimer = setTimeout(() => {
      this.batchTimer = null;
      if (!this.impressionQueue.length) return;
      const batch = this.impressionQueue.splice(0);
      const impressionPayload = { responseId: this.responseId, results: batch, banners: [] };
      if (this.tag) impressionPayload.tag = this.tag;
      this.client.send(this.client.url(this.contextType, 'impression'), impressionPayload);
    }, SearchSpringBeaconClient.config.batchFlushDelayMs);
  }

  startViewTimer(el, productId) {
    const timer = setTimeout(() => {
      this.viewTimers.delete(productId);
      if (this.seenIds.has(productId)) return;
      
      this.seenIds.add(productId);
      this.impressionQueue.push(SearchSpringBeaconClient.productFromEl(el));
      this.flushQueue();
    }, SearchSpringBeaconClient.config.impressionDelayMs);
    this.viewTimers.set(productId, timer);
  }

  cancelViewTimer(productId) {
    const timer = this.viewTimers.get(productId);
    if (timer) {
      clearTimeout(timer);
      this.viewTimers.delete(productId);
    }
  }

  onEntry(entry) {
    const el = entry.target;
    const productId = el.dataset.productId;
    if (!productId) return;

    if (entry.isIntersecting) {
      if (this.seenIds.has(productId) || this.viewTimers.has(productId)) return;
      if (!SearchSpringBeaconClient.isVisible(el)) return;
      this.startViewTimer(el, productId);
    } 
    else {
      this.cancelViewTimer(productId);
    }
  }

  destroy() {
    this.observer.disconnect();
    this.viewTimers.forEach(t => clearTimeout(t));
    if (this.batchTimer) clearTimeout(this.batchTimer);
  }
}

class ClickThroughTracker {
  constructor(container, contextType, responseId, client, tag) {
    this.container = container;
    this.contextType = contextType;
    this.responseId = responseId;
    this.client = client;
    this.tag = tag;

    this.handleClick = this.onClick.bind(this);
    container.addEventListener('click', this.handleClick);
  }

  onClick(e) {
    const isProductLink = e.target.closest('[data-ss-beacon="product-link"]');
    const isQuickView = e.target.closest('[data-ss-beacon="quick-view"]');
    if (!isProductLink && !isQuickView) return;

    const card = e.target.closest('[data-product-id]');
    if (!card) return;

    const product = SearchSpringBeaconClient.productFromEl(card);
    if (card.dataset.ssId) product.uid = card.dataset.ssId;
    const clickPayload = { responseId: this.responseId, results: [product] };
    if (this.tag) clickPayload.tag = this.tag;
    this.client.send(this.client.url(this.contextType, 'clickthrough'), clickPayload);
  }

  destroy() {
    this.container.removeEventListener('click', this.handleClick);
  }
}

class SearchSpringBeacon {
  constructor() {
    this.client = new SearchSpringBeaconClient();
    this.registry = new WeakMap();
    document.addEventListener('searchspring:rendered', e => this.onRendered(e));
    document.addEventListener('searchspring:redirect', e => this.onRedirect(e));
  }

  onRedirect(e) {
    const { responseId, contextType } = e.detail;
    this.client.send(this.client.url(contextType, 'redirect'), { responseId });
  }

  onRendered(e) {
    const container = e.target;
    if (!container || !e.detail) return;

    const existing = this.registry.get(container);
    if (existing) existing.destroy();

    const { responseId, contextType, tag } = e.detail;

    if (!responseId && SearchSpringCommon.isDevMode()) {
      console.warn('[SS Beacon] No responseId in render event — check beacon=true is reaching the API.');
    }

    const renderPayload = { responseId };
    if (tag) renderPayload.tag = tag;
    this.client.send(this.client.url(contextType, 'render'), renderPayload);

    const impressions = new ImpressionTracker(container, contextType, responseId, this.client, tag);
    const clickthrough = new ClickThroughTracker(container, contextType, responseId, this.client, tag);

    this.registry.set(container, {
      destroy() {
        impressions.destroy();
        clickthrough.destroy();
      }
    });
  }
}

new SearchSpringBeacon();